/**
 * useDeviceOrientation - デバイスの向きを検出するカスタムフック
 */

import { useState, useEffect } from 'react'

export type DeviceOrientation = 'portrait' | 'landscape' | 'unknown'

export function useDeviceOrientation(): DeviceOrientation {
  // SSR/hydration対応 - 初期値を適切に設定
  const [orientation, setOrientation] = useState<DeviceOrientation>(() => {
    if (typeof window === 'undefined') return 'unknown'
    
    // 初期値をクライアントサイドで設定
    const win = window as Window & { orientation?: number }
    if ('orientation' in window && typeof win.orientation === 'number') {
      const angle = Math.abs(win.orientation)
      return angle === 90 || angle === 270 ? 'landscape' : 'portrait'
    } else if (window.matchMedia) {
      const isLandscape = window.matchMedia('(orientation: landscape)').matches
      return isLandscape ? 'landscape' : 'portrait'
    } else {
      return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    }
  })

  useEffect(() => {
    // SSRチェック
    if (typeof window === 'undefined') return
    
    const checkOrientation = () => {
      // window.orientationが使える場合（モバイル）
      const win = window as Window & { orientation?: number }
      if ('orientation' in window && typeof win.orientation === 'number') {
        const angle = Math.abs(win.orientation)
        setOrientation(angle === 90 || angle === 270 ? 'landscape' : 'portrait')
      }
      // MediaQueryを使う場合（デスクトップ含む）
      else if (window.matchMedia) {
        const isLandscape = window.matchMedia('(orientation: landscape)').matches
        setOrientation(isLandscape ? 'landscape' : 'portrait')
      }
      // サイズから判定
      else {
        setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait')
      }
    }

    // イベントリスナー登録
    window.addEventListener('orientationchange', checkOrientation)
    window.addEventListener('resize', checkOrientation)

    // MediaQueryリスナー
    const mediaQuery = window.matchMedia('(orientation: landscape)')
    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      setOrientation(e.matches ? 'landscape' : 'portrait')
    }
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaQueryChange)
    } else {
      // 古いブラウザ用
      mediaQuery.addListener(handleMediaQueryChange)
    }

    // クリーンアップ
    return () => {
      window.removeEventListener('orientationchange', checkOrientation)
      window.removeEventListener('resize', checkOrientation)
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaQueryChange)
      } else {
        mediaQuery.removeListener(handleMediaQueryChange)
      }
    }
  }, [])

  return orientation
}